import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import ToggleButton from '../../../../../common/ToggleButton/ToggleButton';
import styles from './CreditBureauTab.module.scss';
import DirectorDetails, { IDirectorDetails } from '../DirectorDetails/DirectorDetails';
import { USER_TYPE } from '../../../../../utils/constants/user';
import { useAppSelector } from '../../../../../app/hooks';
import useGiveConsentLogic from './useGiveConsentLogic';
import { BureauType } from '../../../../Dashboard/DashboardModules/CreditBureau/components/Report/CreditBureauConstants';
import { fetchUploadJourneyData } from '../../../../../store/company/action';
import AddDirectorModal from '../../../../../common/AddDirectorModal/AddDirectorModal';

export const DIRECT_CIBIL_DATA = 'directorCibilData';

const CreditBureauTab = ({
    directorCibilData,
    updateFunction,
    userType,
    assesseeOrgId,
    categoryId,
    flowId,
    managementData,
    isInvestor,
    initialBureau,
}) => {
    const dispatch = useDispatch();
    const [lenderConsent, setLenderConsent] = useState(directorCibilData?.lenderDirectorCibilConsent || false);
    const [borrowerConsent, setBorrowerConsent] = useState(directorCibilData?.borrowerDirectorCibilConsent || false);
    const [consent, setConsent] = useState<boolean>(false);
    const [addDirectorOpen, setAddDirectorOpen] = useState(false);
    const hasCibiReportsData = directorCibilData?.lenderDirectorCibilReports?.filter((report) => !report?.isDeleted);

    const investorData = useAppSelector((state) => state.investor);
    const companyData = useAppSelector((state) => state.company);

    // When an internal or lender user opens this borrower flow via "View Account"
    // (summon), the summoned session stores the borrower's auth token in
    // sessionStorage. We use this as the durable signal — same flag the header
    // uses to hide the View Account CTA itself (HeaderButtonContainer.tsx) — and
    // pass it down so Give Consent renders disabled in those sessions. The
    // borrower's own real login leaves sessionStorage empty and the CTA stays
    // active.
    const isSummonedSession = typeof window !== 'undefined' && !!window.sessionStorage?.getItem('auth_token');

    const [directorDetails, setDirectorDetails] = useState<Record<string, IDirectorDetails[]>>({});

    const managementList = useAppSelector((state: any) => state.company?.uploadJourneyData?.cibilSoftPullData?.managementList);

    const { giveConsent, latestConsentsByDirectorId, receivedDirectorIds, consentRequired, isBulkLoading, giveConsentForAll } =
        useGiveConsentLogic({ assesseeOrgId });

    const getCreditBureauConfig = (userType: string) => {
        if (userType === USER_TYPE.INVESTOR) {
            return investorData?.config?.tabConfigs?.creditBureau?.tabsToDisplay;
        } else if (userType === USER_TYPE.COMPANY) {
            return companyData?.config?.tabConfigs?.creditBureau?.tabsToDisplay;
        }
        return {};
    };
    const creditBureauConfig = getCreditBureauConfig(userType);

    const creditBureaus =
        Object.values(creditBureauConfig)?.filter((config: any) => config?.isActive && config?.isDirectorInputToBeTaken) || [];

    const bureauNames = creditBureaus?.map((config: any) => config?.name) || [];

    const [selectedBureau, setSelectedBureau] = useState<string>(() =>
        initialBureau && initialBureau !== BureauType.ALL && bureauNames.includes(initialBureau)
            ? initialBureau
            : (creditBureaus[0] as any)?.name ?? '',
    );

    const formatDataAndUpdateState = (managementList: any) => {
        const list = managementList.map((director: any) => {
            return {
                directorName: director.name,
                phoneNo: director.phoneNumber,
                consentStatus: director.cibilConsentStatus,
                lastInviteSentAt: director.lastInviteSentAt,
                updatedAt: director.updatedAt,
                cibilConsentStatus: director.cibilConsentStatus,
                isNewDir: false,
                pan: director?.primaryPan,
                // RECUR-27297 fix: `director.id` is the ManagementProfile's own _id — the id
                // consent (captureBorrowerConsent, latestConsentsByDirectorId) and the gate are
                // keyed on. `director.directorId`, when present, is a SEPARATE field UE stamps
                // after a fetch to link this profile to its bureau-identity record in the
                // `directors` collection (a different id space, used server-side so polus can
                // group a co-applicant with its bureau record). Preferring it here — the
                // previous `director.directorId || director.id` — made every consent lookup for
                // an already-fetched director resolve against the wrong id: `captureBorrowerConsent`
                // 400s ("Director not found for the given orgId") on that id, and any local
                // lastConsent lookup misses, so Fetch/Refetch stays permanently blocked even
                // after real consent exists.
                directorId: director.id,
            };
        });
        const bureauNameDirectorMap = {};
        for (const bureauName of bureauNames) {
            bureauNameDirectorMap[bureauName] = list.map((item: any) => ({ ...item }));
        }
        setDirectorDetails(bureauNameDirectorMap);
    };

    useEffect(() => {
        if (managementList) {
            formatDataAndUpdateState(managementList);
        }
    }, [managementList]);

    useEffect(() => {
        setLenderConsent(directorCibilData?.lenderDirectorCibilConsent || false);
        setBorrowerConsent(directorCibilData?.borrowerDirectorCibilConsent || false);
    }, [directorCibilData]);

    const updateLenderCibilConsent = () => {
        const newConsentValue = !lenderConsent;
        setLenderConsent(newConsentValue);
        updateFunction(
            {
                lenderDirectorCibilConsent: newConsentValue,
            },
            DIRECT_CIBIL_DATA,
        );
    };

    const isConsentStatusTrue = (consentStatusObject: any, key: string | null = null) => {
        if (consentStatusObject && typeof consentStatusObject === 'object') {
            if (key) return consentStatusObject[key] === true;
            return Object.values(consentStatusObject).some((value: any) => value?.isConsentGiven === true);
        }
        return false;
    };

    // The single bureau config to render the DirectorDetails block for. Undefined when no
    // bureau is active (empty list or stale selection from a since-deactivated bureau).
    const activeBureauConfig = creditBureaus.find((config: any) => config.name === selectedBureau) as any;

    // All bureaus rendered as clickable tabs above the DirectorDetails block.
    const pills = creditBureaus.map((config: any) => ({
        name: config.name,
        displayName: config.displayName || config.name,
        logoUrl: config.logoUrl,
    }));

    return (
        <>
            {userType === USER_TYPE.INVESTOR ? (
                <div className={styles.Top}>
                    We have consent to fetch bureau details for directors
                    <ToggleButton
                        checked={borrowerConsent ? borrowerConsent : lenderConsent}
                        onChange={updateLenderCibilConsent}
                        disabled={
                            (lenderConsent || borrowerConsent) &&
                            (directorCibilData?.lenderDirectorCibilReports?.length > 0 ||
                                consent ||
                                managementData?.some((item) => isConsentStatusTrue(item.cibilConsentStatus)) ||
                                directorCibilData?.borrowerDirectorVerified)
                        }
                    />
                </div>
            ) : (
                <div></div>
            )}

            {(lenderConsent || userType === USER_TYPE.COMPANY) && creditBureaus.length > 0 && (
                <>
                    {userType !== USER_TYPE.COMPANY && (
                        <div className={styles.PillsRow}>
                            {pills.map((pill) => (
                                <button
                                    key={pill.name}
                                    className={`${styles.Pill} ${selectedBureau === pill.name ? styles.PillActive : ''}`}
                                    onClick={() => setSelectedBureau(pill.name)}
                                >
                                    {pill.logoUrl ? (
                                        <img src={pill.logoUrl} alt={pill.displayName} className={styles.PillLogo} />
                                    ) : (
                                        pill.displayName
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {activeBureauConfig && (
                        <div
                            key={activeBureauConfig.name}
                            // RECUR-27297 — the borrower view owns its own seamless border/card
                            // (DirectorDetails' .BorrowerCard); this wrapper's own border+30px
                            // padding would nest a second box around it and leave a visible gap
                            // above the banner, so it's dropped for the borrower. Not gated on
                            // consentRequired — the borrower consent UI renders unconditionally
                            // (product call, 2026-08-06).
                            style={
                                isInvestor
                                    ? { width: '100%', border: 'none' }
                                    : userType === USER_TYPE.COMPANY
                                    ? { padding: 0, border: 'none' }
                                    : {}
                            }
                            className={styles.Equafix}
                        >
                            <DirectorDetails
                                isAdminSide={userType !== USER_TYPE.COMPANY}
                                setConsent={setConsent}
                                creditBureauConfig={activeBureauConfig}
                                directorDetails={directorDetails[activeBureauConfig.name] || []}
                                setDirectorDetails={setDirectorDetails}
                                giveConsent={giveConsent}
                                latestConsentsByDirectorId={latestConsentsByDirectorId}
                                receivedDirectorIds={receivedDirectorIds}
                                disableGiveConsent={isSummonedSession}
                                consentRequired={consentRequired}
                                isBulkLoading={isBulkLoading}
                                giveConsentForAll={giveConsentForAll}
                                onAddDirectorClick={() => setAddDirectorOpen(true)}
                            />
                        </div>
                    )}
                </>
            )}
            <AddDirectorModal
                open={addDirectorOpen}
                onClose={() => setAddDirectorOpen(false)}
                onSuccess={() => {
                    setAddDirectorOpen(false);
                    dispatch(fetchUploadJourneyData({ assesseeOrgId }));
                }}
                organizationId={assesseeOrgId}
            />
        </>
    );
};

export default CreditBureauTab;
